import jsQR from "jsqr";

const E2E_QR_PAYLOAD_KEY = "__FRONTDESK_E2E_QR_PAYLOAD__";

export interface BrowserSetupScannerHandle {
  stop: () => void;
}

export interface StartBrowserSetupScannerOptions {
  readonly video: HTMLVideoElement;
  readonly onCameraReady?: () => void;
  readonly onDecode: (rawValue: string) => void;
}

declare global {
  interface Window {
    __FRONTDESK_E2E_QR_PAYLOAD__?: string;
  }
}

/**
 * Uses getUserMedia + video + canvas + jsQR instead of BarcodeDetector so the
 * decoder choice stays explicit and remains compatible with iPad Safari's
 * camera stack even when BarcodeDetector support is inconsistent.
 */
export async function startBrowserSetupScanner({
  video,
  onCameraReady,
  onDecode,
}: StartBrowserSetupScannerOptions): Promise<BrowserSetupScannerHandle> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("Camera access is not available on this browser.");
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      facingMode: {
        ideal: "environment",
      },
    },
  });

  let animationFrameId = 0;
  let stopped = false;

  const stop = () => {
    if (stopped) {
      return;
    }

    stopped = true;
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
    }
    stream.getTracks().forEach((track) => track.stop());
    if (video.srcObject === stream) {
      video.srcObject = null;
    }
  };

  const e2ePayload = consumeE2EPayload();
  if (e2ePayload) {
    setTimeout(() => {
      if (!stopped) {
        stop();
        onDecode(e2ePayload);
      }
    }, 0);
    return { stop };
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { willReadFrequently: true });

  if (!context) {
    stop();
    throw new Error("The browser could not start the QR scanner preview.");
  }

  video.srcObject = stream;
  video.muted = true;
  video.setAttribute("playsinline", "true");
  try {
    await video.play();
  } catch (error) {
    stop();
    throw new Error(
      "The browser could not start the QR scanner preview. Continue with manual setup instead.",
      { cause: error },
    );
  }
  onCameraReady?.();

  const tick = () => {
    if (stopped) {
      return;
    }

    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      animationFrameId = requestAnimationFrame(tick);
      return;
    }

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) {
      animationFrameId = requestAnimationFrame(tick);
      return;
    }

    canvas.width = width;
    canvas.height = height;
    context.drawImage(video, 0, 0, width, height);
    const imageData = context.getImageData(0, 0, width, height);
    const result = jsQR(imageData.data, width, height, {
      inversionAttempts: "dontInvert",
    });

    if (result?.data) {
      stop();
      onDecode(result.data);
      return;
    }

    animationFrameId = requestAnimationFrame(tick);
  };

  animationFrameId = requestAnimationFrame(tick);

  return { stop };
}

function consumeE2EPayload(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const payload = window[E2E_QR_PAYLOAD_KEY];
  if (!payload) {
    return null;
  }

  delete window[E2E_QR_PAYLOAD_KEY];
  return payload;
}
